import { useState, useMemo, useEffect } from 'react';
import { Search, Plus, Menu, Bot, Wrench, Tag, CalendarDays, User } from 'lucide-react';
import { FavoriteButton } from '@/components/FavoriteButton';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { useAgents } from '@/hooks/useAgents';
import { useDirectResourcePermission, usePermissions, useResourcePermissions } from '@/hooks/usePermissions';
import { useProfile } from '@/hooks/useProfile';
import { useScopeOptions } from '@/hooks/useScopeOptions';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
// AgentFormModal replaced by full-page AgentFormPage
import { getProjectIdFromFilter } from '@/types/navigation';
import type { Tables } from '@/integrations/supabase/types';

const AgentCard = ({
  agent,
  ownerName,
  isAdmin,
  onEdit,
  onUse,
}: {
  agent: Tables<'agents'>;
  ownerName?: string;
  isAdmin: boolean;
  onEdit: (a: Tables<'agents'>) => void;
  onUse: (a: Tables<'agents'>) => void;
}) => {
  const { canEdit } = useResourcePermissions(agent.owner_id, agent.project_id ?? undefined);
  const { canEdit: canEditDirect } = useDirectResourcePermission('agent', agent.id);
  const canEditAgent = canEdit || canEditDirect || isAdmin;

  return (
    <div className="bg-surface border border-color rounded-xl p-5 hover:border-brand-primary hover:shadow-lg transition-all flex flex-col h-full">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 pr-2">
          <h3 className="text-base font-semibold text-primary">{agent.name}</h3>
          {agent.status === 'beta' && (
            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[hsl(var(--status-beta-bg))] text-[hsl(var(--status-beta-text))]">
              Beta
            </span>
          )}
        </div>
        <div className="shrink-0">
          <FavoriteButton targetId={agent.id} targetType="agent" />
        </div>
      </div>

      <p className="text-sm text-secondary mb-4 line-clamp-2 flex-1">
        {agent.description || 'Sem descrição'}
      </p>

      <div className="space-y-2 text-xs text-tertiary mb-4">
        <div className="flex items-center gap-2">
          <Wrench className="w-3 h-3" />
          <span>{agent.model}</span>
        </div>
        <div className="flex items-center gap-2">
          <Tag className="w-3 h-3" />
          <span className="capitalize">{agent.scope_type === 'global' ? 'Espaço Caldeira' : agent.scope_type === 'personal' ? 'Pessoal' : agent.scope_type}</span>
        </div>
        <div className="flex items-center gap-2">
          <CalendarDays className="w-3 h-3" />
          <span>Atualizado {new Date(agent.updated_at).toLocaleDateString('pt-BR')}</span>
        </div>
        {ownerName && (
          <div className="flex items-center gap-2">
            <User className="w-3 h-3" />
            <span>Dono: {ownerName}</span>
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-auto">
        <button
          onClick={() => onUse(agent)}
          className="flex-1 px-4 py-2 brand-primary rounded-md text-sm font-medium"
        >
          Usar
        </button>
        {canEditAgent && (
          <button
            onClick={() => onEdit(agent)}
            className="px-4 py-2 border border-color rounded-md text-sm text-primary hover:bg-hover"
          >
            Editar
          </button>
        )}
      </div>
    </div>
  );
};

export const AgentsPage = () => {
  const { user } = useAuth();
  const { navigation, sidebarCollapsed, setSidebarCollapsed, setActiveAgentId, setActiveConversationId, navigateToTab, navigateToProject, navigateToPersonal, navigateToGlobal, scopeFilter, setScopeFilter, searchPrefill, setSearchPrefill, setEditingResource } = useNavigation();
  const { canCreate } = usePermissions();
  const { isAdmin } = useProfile();
  const scopeOptions = useScopeOptions();

  const projectId = navigation.projectId;

  // Fetch all relevant scopes (global includes project items when projectId set)
  const { data: globalAgents = [], isLoading: loadingGlobal, error: errorGlobal } = useAgents('global', undefined, projectId);
  const { data: personalAgents = [], isLoading: loadingPersonal, error: errorPersonal } = useAgents('personal');

  const isLoading = loadingGlobal || loadingPersonal;
  const error = errorGlobal || errorPersonal;

  // Deduplicate
  const allAgents = useMemo(() => {
    const map = new Map<string, Tables<'agents'>>();
    globalAgents.forEach(a => map.set(a.id, a));
    personalAgents.forEach(a => map.set(a.id, a));
    return Array.from(map.values());
  }, [globalAgents, personalAgents]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Consume searchPrefill from command palette
  useEffect(() => {
    if (searchPrefill) {
      setSearch(searchPrefill);
      setSearchPrefill('');
    }
  }, [searchPrefill, setSearchPrefill]);
  // Modal state no longer needed - using full-page form

  const filtered = useMemo(() => {
    let list = allAgents;
    const filterProjectId = getProjectIdFromFilter(scopeFilter);

    if (filterProjectId) {
      list = list.filter(a => a.project_id === filterProjectId);
    } else if (scopeFilter === 'global') {
      list = list.filter(a => a.scope_type === 'global' && !a.project_id);
    } else if (scopeFilter === 'personal') {
      list = list.filter(a => a.scope_type === 'personal' && !a.project_id);
    }

    if (statusFilter !== 'all') list = list.filter(a => a.status === statusFilter);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        a => a.name.toLowerCase().includes(q) || a.description?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [allAgents, scopeFilter, statusFilter, search]);

  const ownerIds = useMemo(() => {
    return Array.from(new Set(filtered.map(agent => agent.owner_id).filter(ownerId => ownerId !== user?.id)));
  }, [filtered, user?.id]);

  const { data: ownerProfiles = [] } = useQuery({
    queryKey: ['agent-owner-profiles', ownerIds],
    enabled: ownerIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, nome, email')
        .in('id', ownerIds);
      if (error) throw error;
      return data ?? [];
    },
  });

  const ownerNameById = useMemo(() => {
    return new Map(ownerProfiles.map(profile => [profile.id, profile.nome || profile.email]));
  }, [ownerProfiles]);

  const openCreate = () => {
    setEditingResource({ type: 'agent' });
  };

  const openEdit = (agent: Tables<'agents'>) => {
    setEditingResource({ type: 'agent', id: agent.id });
  };

  const handleUse = (agent: Tables<'agents'>) => {
    // Switch to the agent's environment before activating
    if (agent.project_id) {
      navigateToProject(agent.project_id);
    } else if (agent.scope_type === 'personal') {
      navigateToPersonal();
    } else if (agent.scope_type === 'global') {
      navigateToGlobal();
    }
    setActiveAgentId(agent.id);
    setActiveConversationId(null);
    navigateToTab('chat');
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-8 py-6 border-b border-color shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            {sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(false)}
                className="p-2 hover:bg-hover rounded-md"
              >
                <Menu className="w-5 h-5" />
              </button>
            )}
            <Bot className="w-5 h-5 text-primary" />
            <h1 className="text-xl font-semibold text-primary">Agentes</h1>
          </div>
          {canCreate && (
            <button
              onClick={openCreate}
              className="px-4 py-2 brand-primary rounded-md text-sm font-medium flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Novo Agente
            </button>
          )}
        </div>

        {/* Scope filter tabs */}
        <div className="flex items-center gap-1 mb-4">
          {scopeOptions.map((scope) => (
            <button
              key={scope.id}
              onClick={() => setScopeFilter(scope.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                scopeFilter === scope.id
                  ? 'bg-brand-primary text-white border-transparent'
                  : 'border-color text-secondary hover:bg-hover'
              }`}
            >
              {scope.icon}
              {scope.label}
            </button>
          ))}
        </div>

        <p className="text-sm text-secondary mb-4">
          {isLoading ? '...' : `${filtered.length} agente${filtered.length !== 1 ? 's' : ''} encontrado${filtered.length !== 1 ? 's' : ''}`}
        </p>

        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="w-full h-10 pl-10 pr-4 rounded-md border border-color bg-surface text-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-md border border-color bg-surface text-sm text-secondary focus:outline-none focus:ring-2 focus:ring-brand-primary"
          >
            <option value="all">Status</option>
            <option value="active">Ativo</option>
            <option value="beta">Beta</option>
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        {error && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Bot className="w-10 h-10 text-tertiary mb-4" />
            <h3 className="text-lg font-semibold text-primary mb-2">Erro ao carregar agentes</h3>
            <p className="text-sm text-secondary">{(error as Error).message}</p>
          </div>
        )}

        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="border border-color rounded-xl p-5 space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="w-12 h-12 rounded-lg" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-2/3" />
                <div className="flex gap-2 pt-2">
                  <Skeleton className="h-9 flex-1 rounded-md" />
                  <Skeleton className="h-9 w-20 rounded-md" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Bot className="w-10 h-10 text-tertiary mb-4" />
            <h3 className="text-lg font-semibold text-primary mb-2">
              {allAgents.length === 0 ? 'Nenhum agente configurado' : 'Nenhum resultado'}
            </h3>
            <p className="text-sm text-secondary mb-6">
              {allAgents.length === 0
                ? 'Crie seu primeiro agente para começar.'
                : 'Tente ajustar os filtros de busca.'}
            </p>
            {allAgents.length === 0 && canCreate && (
              <button
                onClick={openCreate}
                className="px-4 py-2 brand-primary rounded-md text-sm font-medium flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Novo Agente
              </button>
            )}
          </div>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {filtered.map(agent => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  ownerName={agent.owner_id !== user?.id ? ownerNameById.get(agent.owner_id) : undefined}
                  isAdmin={isAdmin}
                  onEdit={openEdit}
                  onUse={handleUse}
                />
              ))}
            </div>

          </>
        )}
      </div>

    </div>
  );
};
